// ABOUTME: Hook for managing workspace invites via tRPC queries and mutations.
// ABOUTME: Provides invite list, create, cancel, and resend functionality.

import { useCallback } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { trpc } from '@/lib/trpc';
import type {
    CreateInviteRequest,
    CreateInviteResponse,
} from '@/types/workspace';

export function useWorkspaceInvites() {
    const { currentWorkspace, isAdmin } = useWorkspace();
    const workspaceId = currentWorkspace?.id;
    const utils = trpc.useUtils();

    const invitesQuery = trpc.workspaceInvites.list.useQuery(
        { workspaceId: workspaceId! },
        {
            enabled: !!workspaceId && isAdmin,
            select: (data) => ((data as any).invites ?? data),
        }
    );

    const createMutation = trpc.workspaceInvites.create.useMutation({
        onSuccess: () => {
            utils.workspaceInvites.list.invalidate();
        },
    });

    const cancelMutation = trpc.workspaceInvites.cancel.useMutation({
        onSuccess: () => {
            utils.workspaceInvites.list.invalidate();
        },
    });

    const resendMutation = trpc.workspaceInvites.resend.useMutation({
        onSuccess: () => {
            utils.workspaceInvites.list.invalidate();
        },
    });

    const createInvite = useCallback(async (
        data: CreateInviteRequest
    ): Promise<CreateInviteResponse | null> => {
        if (!workspaceId || !isAdmin) return null;
        const result = await createMutation.mutateAsync({ workspaceId, ...data });
        return result as unknown as CreateInviteResponse;
    }, [workspaceId, isAdmin, createMutation]);

    const cancelInvite = useCallback(async (inviteId: string): Promise<boolean> => {
        if (!workspaceId || !isAdmin) return false;
        try {
            await cancelMutation.mutateAsync({ workspaceId, inviteId });
            return true;
        } catch (err) {
            console.error('Failed to cancel invite:', err);
            return false;
        }
    }, [workspaceId, isAdmin, cancelMutation]);

    const resendInvite = useCallback(async (
        inviteId: string
    ): Promise<CreateInviteResponse | null> => {
        if (!workspaceId || !isAdmin) return null;
        try {
            const result = await resendMutation.mutateAsync({ workspaceId, inviteId });
            return result as unknown as CreateInviteResponse;
        } catch (err) {
            console.error('Failed to resend invite:', err);
            return null;
        }
    }, [workspaceId, isAdmin, resendMutation]);

    const invites: any[] = invitesQuery.data ?? [];

    return {
        invites,
        loading: invitesQuery.isLoading,
        error: invitesQuery.error?.message ?? createMutation.error?.message ?? null,
        refetch: () => { utils.workspaceInvites.list.invalidate(); },
        createInvite,
        cancelInvite,
        resendInvite,
    };
}
